/**
 * TASK-ATTR-011: Effect Sprite Pool
 *
 * Object pool for effect sprites to reduce garbage collection.
 * Reuses sprite instances by attributeId to minimize allocation overhead.
 */

import * as PIXI from 'pixi.js';
import { EffectSpriteFactory } from './effect-sprite-factory';

/**
 * Pooled sprite entry
 */
interface PooledSprite {
  attributeId: string;
  sprite: PIXI.AnimatedSprite;
  inUse: boolean;
}

/**
 * Object pool for effect sprites
 * Reuses sprites by attributeId to minimize garbage collection
 */
export class EffectSpritePool {
  private factory: EffectSpriteFactory;
  private pool: PooledSprite[] = [];
  private maxPoolSize: number;

  constructor(factory: EffectSpriteFactory, maxPoolSize: number = 100) {
    this.factory = factory;
    this.maxPoolSize = maxPoolSize;
  }

  /**
   * Pre-load effect frames into the texture cache
   * Call this during the preload phase to ensure textures are ready
   *
   * @param frames - Array of base64-encoded PNG frames
   */
  async preloadFrames(frames: string[]): Promise<void> {
    await this.factory.preloadFrames(frames);
  }

  /**
   * Acquire a sprite from the pool
   * Reuses an existing sprite for the same attributeId if available
   * Otherwise creates a new sprite
   *
   * @param attributeId - Unique identifier for the effect attribute
   * @param frames - Array of base64-encoded PNG frames
   * @returns PIXI.AnimatedSprite ready to use
   */
  acquire(attributeId: string, frames: string[]): PIXI.AnimatedSprite {
    // Try to find an available sprite for this attributeId
    const available = this.pool.find(
      entry => entry.attributeId === attributeId && !entry.inUse
    );

    if (available) {
      // Reuse existing sprite
      available.inUse = true;

      // Reset sprite state
      available.sprite.gotoAndStop(0);
      available.sprite.alpha = 1.0;
      available.sprite.visible = true;
      available.sprite.loop = false;

      return available.sprite;
    }

    // No available sprite - create a new one
    const sprite = this.factory.createAnimatedSprite(frames);

    // Add to pool if not at max size
    if (this.pool.length < this.maxPoolSize) {
      this.pool.push({
        attributeId,
        sprite,
        inUse: true
      });
    }

    return sprite;
  }

  /**
   * Acquire a sprite from the pool (async version for PixiJS v8)
   * Pre-loads textures before creating the sprite
   *
   * @param attributeId - Unique identifier for the effect attribute
   * @param frames - Array of base64-encoded PNG frames
   * @returns Promise<PIXI.AnimatedSprite> ready to use
   */
  async acquireAsync(attributeId: string, frames: string[]): Promise<PIXI.AnimatedSprite> {
    // Try to find an available sprite for this attributeId
    const available = this.pool.find(
      entry => entry.attributeId === attributeId && !entry.inUse
    );

    if (available) {
      // Reuse existing sprite
      available.inUse = true;

      // Reset sprite state
      available.sprite.gotoAndStop(0);
      available.sprite.alpha = 1.0;
      available.sprite.visible = true;
      available.sprite.loop = false;

      return available.sprite;
    }

    // No available sprite - create a new one using async method
    const sprite = await this.factory.createAnimatedSpriteAsync(frames);

    // Add to pool if not at max size
    if (this.pool.length < this.maxPoolSize) {
      this.pool.push({
        attributeId,
        sprite,
        inUse: true
      });
    }

    return sprite;
  }

  /**
   * Release a sprite back to the pool
   * The sprite becomes available for reuse
   *
   * @param sprite - Sprite to release
   */
  release(sprite: PIXI.AnimatedSprite): void {
    // Find the sprite in the pool
    const entry = this.pool.find(e => e.sprite === sprite);

    if (entry) {
      // Mark as available
      entry.inUse = false;

      // Stop animation
      entry.sprite.stop();

      // Remove from parent container
      if (entry.sprite.parent) {
        entry.sprite.parent.removeChild(entry.sprite);
      }

      // Hide the sprite
      entry.sprite.visible = false;
    }
  }

  /**
   * Prune the pool by removing unused sprites beyond a threshold
   * Call this periodically to prevent the pool from growing too large
   */
  prune(): void {
    // Keep only in-use sprites and a reasonable number of available sprites
    const maxAvailable = Math.floor(this.maxPoolSize * 0.2); // Keep 20% available
    let availableCount = 0;

    this.pool = this.pool.filter(entry => {
      if (entry.inUse) {
        return true; // Keep all in-use sprites
      }

      availableCount++;
      if (availableCount <= maxAvailable) {
        return true; // Keep some available sprites
      }

      // Remove this sprite
      this.factory.disposeSprite(entry.sprite);
      return false;
    });
  }

  /**
   * Get pool statistics
   */
  getStats() {
    const inUse = this.pool.filter(e => e.inUse).length;
    const available = this.pool.length - inUse;

    return {
      available,
      inUse,
      total: this.pool.length,
      maxSize: this.maxPoolSize
    };
  }

  /**
   * Destroy the pool and all sprites
   * Call this during cleanup
   */
  destroy(): void {
    // Destroy all sprites
    for (const entry of this.pool) {
      this.factory.disposeSprite(entry.sprite);
    }

    // Clear the pool
    this.pool = [];
  }
}
